import { Component, lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigationType, useSearchParams } from "react-router-dom";

import ProductPage from "./pages/ProductPage.jsx";
import CartOverlay from "./components/CartOverlay.jsx";
import SearchModal from "./components/SearchModal.jsx";
import GoogleTranslate from "./components/GoogleTranslate.jsx";
import Header from "./components/Header.jsx";
import { MUSIC_ENABLED } from "./config.js";
import "./app.css";
import "./styles/inquiry-button.css";
// Phone and touch layer. Imported last so it wins ties with app.css.
import "./styles/mobile.css";
import { isMobileViewport, isTouchDevice } from "./utils/viewport.js";
import { AppModeProvider } from "./context/AppModeContext.jsx";
import { AuthProvider } from "./context/AuthContext.jsx";
import { SavedProvider } from "./context/SavedContext.jsx";
import { CartProvider } from "./context/CartContext.jsx";
import { FeaturedSearchProvider } from "./context/FeaturedSearchContext.jsx";
import { HeaderProvider, useHeader } from "./context/HeaderContext.jsx";
import { setDisplayedPath } from "./utils/displayedRoute.js";
import { AppNavigationTypeContext } from "./utils/appNavigationType.js";
import { AudioProvider, useAudio } from "./context/AudioContext.jsx";

import FeaturedPage from "./pages/FeaturedPage.jsx";
import FeaturedGatewayPage from "./pages/FeaturedGatewayPage.jsx";
import BrandsPage from "./pages/BrandsPage.jsx";
import HomePage from "./pages/HomePage.jsx";

// Code-split everything that isn't on the hot path (home / brand /
// featured / product). Keeps the initial JS parsed into memory small and
// means we don't pull Stripe + PayPal + admin into every session.
//
// Each deploy renames these files (their names carry a hash of their
// content) and drops the old ones, so a tab opened before a deploy asks for
// a page's old file and the import fails; with nothing to catch it the whole
// app came down to a black screen (the owner, 2026-10-25: "sometimes my cart
// goes black"). lazyPage reloads the site once instead, which fetches the
// new index.html and its new file names, and the reader lands on the page
// they asked for. Once per 10s at most, so a page that really cannot load
// still fails (to PageErrorBoundary) rather than reloading for ever.
const CHUNK_RELOAD_KEY = "chunk-reload-at";
function reloadOnceForNewBuild() {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    if (Date.now() - last < 10000) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}
function lazyPage(load) {
  return lazy(() => load().catch((err) => {
    // Never settles: the page is on its way out.
    if (reloadOnceForNewBuild()) return new Promise(() => {});
    throw err;
  }));
}
// Vite's own signal for the same failure, when a page's preloaded CSS or JS
// file is the one that is gone.
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (event) => {
    if (reloadOnceForNewBuild()) event.preventDefault();
  });
}

// Anything that still throws while drawing a page: a short note and a way to
// reload, under the header, instead of the app unmounting to black. Cleared
// when the reader goes to another page (resetKey). Not keyed on the route:
// a key would remount every page, and the catalog's shared state with it,
// on every navigation.
class PageErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.error("Page failed to render:", error);
  }

  componentDidUpdate(prev) {
    if (this.state.failed && prev.resetKey !== this.props.resetKey) this.setState({ failed: false });
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="page-error" role="alert">
        <p className="page-error__text">This page didn't load.</p>
        <button type="button" className="page-error__reload" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    );
  }
}

const AdminPage = lazyPage(() => import("./pages/AdminPage.jsx"));
const AdminReportsPage = lazyPage(() => import("./pages/AdminReportsPage.jsx"));
const AdminUsersPage = lazyPage(() => import("./pages/AdminUsersPage.jsx"));
const AdminInventoryPage = lazyPage(() => import("./pages/AdminInventoryPage.jsx"));
const BuyNowPage = lazyPage(() => import("./pages/BuyNowPage.jsx"));
const CartPage = lazyPage(() => import("./pages/CartPage.jsx"));
const BillingPage = lazyPage(() => import("./pages/BillingPage.jsx"));
const CheckoutPageV2 = lazyPage(() => import("./pages/CheckoutPageV2.jsx"));
const CheckoutSuccessPage = lazyPage(() => import("./pages/CheckoutSuccessPage.jsx"));
const SavedPage = lazyPage(() => import("./pages/SavedPage.jsx"));
const SigninPage = lazyPage(() => import("./pages/SigninPage.jsx"));
const OrdersPage = lazyPage(() => import("./pages/OrdersPage.jsx"));
const AccountPage = lazyPage(() => import("./pages/AccountPage.jsx"));
const TermsPage = lazyPage(() => import("./pages/TermsPage.jsx"));
const PrivacyPage = lazyPage(() => import("./pages/PrivacyPage.jsx"));
const LegalPage = lazyPage(() => import("./pages/LegalPage.jsx"));
const ProhibitedPage = lazyPage(() => import("./pages/ProhibitedPage.jsx"));
const StoragePage = lazyPage(() => import("./pages/StoragePage.jsx"));
const ContactPage = lazyPage(() => import("./pages/ContactPage.jsx"));
const InfoPage = lazyPage(() => import("./pages/InfoPage.jsx"));
const RentalPage = lazyPage(() => import("./pages/RentalPage.jsx"));
const SellPage = lazyPage(() => import("./pages/SellPage.jsx"));
const AuthenticityPage = lazyPage(() => import("./pages/AuthenticityPage.jsx"));
const ProxyDashboard = lazyPage(() => import("./pages/proxy/ProxyDashboard.jsx"));
const QuotePage = lazyPage(() => import("./pages/proxy/QuotePage.jsx"));
const BoxManagementPage = lazyPage(() => import("./pages/proxy/BoxManagementPage.jsx"));
const CreateQuotePage = lazyPage(() => import("./pages/proxy/CreateQuotePage.jsx"));

function RedirectWithQuery({ to }) {
  const location = useLocation();
  const search = location.search || "";
  const hash = location.hash || "";
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

function FeaturedSearchRoute() {
  const [params] = useSearchParams();
  const location = useLocation();
  const q = params.get("q") || "";
  // A new search is a fresh page (the key). The page writing a query typed
  // into its own search field into the address (state.keepSearchPage,
  // FeaturedPage) is not: the field keeps focus and the filters stay.
  const keyRef = useRef(null);
  const lastQ = useRef(q);
  if (keyRef.current === null || (q !== lastQ.current && !location.state?.keepSearchPage)) {
    keyRef.current = `search-${q}`;
  }
  lastQ.current = q;
  return <FeaturedPage key={keyRef.current} searchTerm={q} />;
}


function SearchOverlay() {
  const [data, setData] = useState(null);
  const [fading, setFading] = useState(false);
  const textRef = useRef(null);
  const fillRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const { query, rect, fontSize, fontWeight, letterSpacing, textAlign, lineHeight } = e.detail;
      setData({ query, rect, fontSize, fontWeight, letterSpacing, textAlign, lineHeight });
      setFading(false);
      if (fillRef.current) cancelAnimationFrame(fillRef.current);
      // Build spans after render
      requestAnimationFrame(() => {
        const el = textRef.current;
        if (!el) return;
        const chars = query.slice(0, 20).split("");
        el.textContent = "";
        const frag = document.createDocumentFragment();
        const spans = chars.map((ch) => {
          const span = document.createElement("span");
          span.textContent = ch;
          span.style.color = "#555";
          frag.appendChild(span);
          return span;
        });
        el.appendChild(frag);
        const total = spans.length;
        const duration = 1200;
        const start = performance.now();
        const ease = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
        const animate = (now) => {
          const elapsed = now - start;
          const progress = Math.min(elapsed / duration, 1);
          const cursor = ease(progress) * (total + 0.5);
          for (let i = 0; i < total; i++) {
            const dist = cursor - i;
            if (dist >= 1) {
              spans[i].style.color = "#fff";
            } else if (dist > 0) {
              const v = Math.round(85 + dist * 170);
              spans[i].style.color = `rgb(${v},${v},${v})`;
            }
          }
          if (progress < 1) {
            fillRef.current = requestAnimationFrame(animate);
          } else {
            setTimeout(() => {
              setFading(true);
              setTimeout(() => setData(null), 600);
            }, 300);
          }
        };
        fillRef.current = requestAnimationFrame(animate);
      });
    };
    window.addEventListener("landing-search", handler);
    return () => {
      window.removeEventListener("landing-search", handler);
      if (fillRef.current) cancelAnimationFrame(fillRef.current);
    };
  }, []);

  if (!data) return null;

  const textStyle = data.rect ? {
    position: "fixed",
    top: data.rect.top + "px",
    left: data.rect.left + "px",
    width: data.rect.width + "px",
    height: data.rect.height + "px",
    fontSize: data.fontSize,
    fontWeight: data.fontWeight,
    letterSpacing: data.letterSpacing,
    textAlign: data.textAlign,
    lineHeight: data.lineHeight,
  } : {};

  return (
    <div className={`search-overlay${fading ? " search-overlay--fading" : ""}`}>
      <div className="search-overlay__text" ref={textRef} style={textStyle} />
    </div>
  );
}

// The first path this session opened. Read at MODULE load, before the
// router has navigated anywhere, so it still names the real entry point
// later on — reading it inside the component would just return whatever
// route is current, and coming back to / would look like a cold open.
const ENTRY_PATH = typeof window !== "undefined" ? window.location.pathname : "/";
// Matches the fade in app.css (400ms delay + 900ms duration).
const HEADER_INTRO_MS = 1300;

function GlobalHeader() {
  const { collapsed } = useHeader();
  const location = useLocation();
  const [introDone, setIntroDone] = useState(false);
  // Cold open only: this session STARTED on /, and the fade has not run
  // yet. Arriving at / from inside the site is an ordinary navigation, so
  // the bar is simply already there.
  const landingIntro =
    location.pathname === "/" && ENTRY_PATH === "/" && !introDone;

  useEffect(() => {
    if (!landingIntro) return;
    // Latches, so navigating away and back never replays the fade.
    const t = setTimeout(() => setIntroDone(true), HEADER_INTRO_MS);
    return () => clearTimeout(t);
  }, [landingIntro]);

  return <Header collapsed={collapsed} landingIntro={landingIntro} />;
}

function TrapVinyl() {
  const { playing, toggle, analyserRef } = useAudio();
  const location = useLocation();
  const isLanding = location.pathname === "/";
  const isBrandPage = location.pathname === "/showcase";
  const isCartPage = location.pathname === "/featured/cart";
  const barRefs = useRef([]);
  const rafRef = useRef(null);
  const dataRef = useRef(null);
  const buttonRef = useRef(null);
  // Visualizer spans the full width of the site header, so it needs many more
  // bars than the old corner widget.
  const BAR_COUNT = 48;

  useEffect(() => {
    if (!playing) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      for (const bar of barRefs.current) {
        if (bar) bar.style.height = "";
      }
      return;
    }
    const analyser = analyserRef.current;
    if (!analyser) return;

    const bufferLength = analyser.frequencyBinCount;
    if (!dataRef.current || dataRef.current.length !== bufferLength) {
      dataRef.current = new Uint8Array(bufferLength);
    }

    // Right-aligned taper: rightmost bar is tallest, each bar further left
    // tapers down. Shared bass pulse + per-bar independent band for natural
    // flicker.
    const BASS_START = 1;
    const BASS_END = Math.min(24, bufferLength);
    const BASS_BOOST = 1.6;
    const MAX_H = 45;
    // Right-aligned taper that scales to any BAR_COUNT (the visualizer now
    // spans the full width of the site header): the rightmost bar is tallest,
    // tapering smoothly toward a floor on the left. distFromRight is
    // normalized so the curve shape stays the same regardless of bar count.
    const falloffAt = (distFromRight) => {
      const t = BAR_COUNT > 1 ? distFromRight / (BAR_COUNT - 1) : 0;
      return 0.13 + 0.87 * Math.pow(1 - t, 1.8);
    };
    const ownBands = Array.from({ length: BAR_COUNT }, (_, i) => {
      const t = i / BAR_COUNT;
      const start = Math.max(1, Math.round(4 + t * 80));
      const end = Math.max(start + 2, Math.round(4 + ((i + 1) / BAR_COUNT) * 80));
      return { start: Math.min(start, bufferLength - 1), end: Math.min(end, bufferLength), boost: 1.4 + t * 2.2 };
    });
    // Independent-signal weight grows with distance from the right: rightmost
    // locks to the pulse, leftmost can flicker on its own.
    const ownWeightAt = (distFromRight) => {
      const t = BAR_COUNT > 1 ? distFromRight / (BAR_COUNT - 1) : 0;
      return 0.15 + 0.52 * t;
    };

    const tick = () => {
      analyser.getByteFrequencyData(dataRef.current);
      let bassSum = 0;
      for (let j = BASS_START; j < BASS_END; j++) bassSum += dataRef.current[j];
      const bassAvg = bassSum / (BASS_END - BASS_START);
      const bassN = Math.min(1, (bassAvg / 255) * BASS_BOOST);

      for (let i = 0; i < BAR_COUNT; i++) {
        const distFromRight = BAR_COUNT - 1 - i;
        const falloff = falloffAt(distFromRight);
        const { start, end, boost } = ownBands[i];
        let ownSum = 0;
        for (let j = start; j < end; j++) ownSum += dataRef.current[j];
        const ownAvg = ownSum / (end - start);
        const ownN = Math.min(1, (ownAvg / 255) * boost);
        const ownWeight = ownWeightAt(distFromRight);
        const combined = Math.min(1, bassN * falloff * (1 - ownWeight) + ownN * ownWeight);
        const height = Math.max(2, Math.round(2 + combined * MAX_H));
        const bar = barRefs.current[i];
        if (bar) bar.style.height = `${height}px`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [playing, analyserRef]);

  // Early returns must come after all hooks (Rules of Hooks). /brand renders
  // its own per-row audio toggles inside each brand section so they fade in
  // with the row's reveal — hide the global one on that route. The cart page
  // hides the music option entirely.
  if (isBrandPage || isCartPage) return null;

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={toggle}
      aria-label={playing ? "Pause audio" : "Play audio"}
      className={`audio-toggle${playing ? " audio-toggle--playing" : ""}${isLanding ? " audio-toggle--landing" : ""}`}
    >
      <span className="audio-toggle__icon audio-toggle__icon--music" aria-hidden="true">
        <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3z" />
        </svg>
      </span>
      <span className="audio-toggle__icon audio-toggle__icon--play" aria-hidden="true">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 5 L19 12 L7 19 Z" />
        </svg>
      </span>
      <span className="audio-toggle__bars" aria-hidden="true">
        {Array.from({ length: BAR_COUNT }).map((_, i) => (
          <span
            key={i}
            ref={(el) => { barRefs.current[i] = el; }}
            className="audio-toggle__bar"
          />
        ))}
      </span>
    </button>
  );
}

// Black overlay for the hamburger → landing fade. Mounted here (not via
// document.body.appendChild) so it renders inside React's tree.
//
// Fade-in nuance: a fresh DOM node inserted with `opacity:1` won't animate
// from 0 → 1 — CSS transitions only run when a property CHANGES. So we
// mount with the base class (opacity:0) and flip --active on the next
// animation frame to trigger the transition.
function NavFadeOverlay() {
  const { navFadePhase, navFadeOutMs } = useHeader();
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (navFadePhase === "active") {
      const id = requestAnimationFrame(() => setActive(true));
      return () => cancelAnimationFrame(id);
    }
    setActive(false);
  }, [navFadePhase]);

  if (navFadePhase === "idle") return null;

  const classes = ["nav-to-landing-overlay"];
  if (navFadePhase === "overlay-out") classes.push("nav-to-landing-overlay--fade");
  else if (active) classes.push("nav-to-landing-overlay--active");

  // Fade-out length comes from context (CSS keeps the 1500ms landing default).
  const style = navFadePhase === "overlay-out" ? { transitionDuration: `${navFadeOutMs}ms` } : undefined;
  return <div className={classes.join(" ")} style={style} aria-hidden="true" />;
}

function getRouteSection(pathname) {
  if (pathname.startsWith("/featured")) return "featured";
  return pathname;
}

// Catalog tabs that share the Featured layout — transition between them with
// a fade-to-black cut instead of an instant swap.
const CATALOG_PATHS = new Set(["/featured", "/featured/new", "/featured/sold"]);
const isCatalogTab = (pathname) => CATALOG_PATHS.has(pathname);

function PageTransition() {
  const location = useLocation();
  // The real navigation type, read here, outside the scoped Routes below,
  // which report POP for everything (utils/appNavigationType).
  const navigationType = useNavigationType();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [phase, setPhase] = useState("in"); // "in" | "out" | "catalog-out" | "catalog-in"
  const timerRef = useRef(null);
  const timerRef2 = useRef(null);

  useEffect(() => {
    // A URL change that keeps the pathname — a search writing ?q=, a state
    // clear via replace — must still reach the page. The routes render
    // displayLocation, and inside <Routes location=…> the page reads its
    // search params from THAT location, so without this sync a search was
    // never seen by the catalog at all.
    //
    // It must also leave a fade in progress alone. The previous version
    // returned a cleanup that cancelled the restore timers on EVERY location
    // change, so a URL update landing inside the fade (which a search does)
    // cancelled the swap-and-restore and left the phase at "catalog-out", the
    // grid animation holding at opacity 0 — the page going black after a
    // search. Timers are now cleared only when a NEW transition supersedes a
    // pending one, plus once on unmount below.
    if (location.pathname === displayLocation.pathname) {
      if (location !== displayLocation) setDisplayLocation(location);
      return;
    }

    const oldSection = getRouteSection(displayLocation.pathname);
    const newSection = getRouteSection(location.pathname);
    clearTimeout(timerRef.current);
    clearTimeout(timerRef2.current);

    // Catalog tab ↔ tab (featured ↔ new ↔ sold): fade the listings to black, swap, fade back in
    // Timings sync with app.css keyframes:
    //   catalog-out: 180ms, reaches solid black at 60% (~108ms) and holds to 180ms.
    //   Swap fires at 150ms — inside the black hold — so the old grid is fully
    //   invisible before the new one replaces it.
    //   catalog-in: 500ms, holds black for the first 25% (~125ms) then emerges.
    if (isCatalogTab(displayLocation.pathname) && isCatalogTab(location.pathname)) {
      setPhase("catalog-out");
      timerRef.current = setTimeout(() => {
        setDisplayLocation(location);
        setPhase("catalog-in");
        timerRef2.current = setTimeout(() => setPhase("in"), 500);
      }, 150);
      return;
    }

    // Same section, not catalog tabs (e.g. deep routes inside /featured): instant swap.
    // Also restores "in" in case a phase was left behind.
    if (oldSection === newSection) {
      setDisplayLocation(location);
      setPhase("in");
      return;
    }

    // Different section: fade out, swap, fade in
    setPhase("out");
    timerRef.current = setTimeout(() => {
      setDisplayLocation(location);
      setPhase("in");
    }, 150);
  }, [location, displayLocation]);

  // The only unconditional cancel: on unmount.
  useEffect(() => () => {
    clearTimeout(timerRef.current);
    clearTimeout(timerRef2.current);
  }, []);
  // Publish the page actually on screen (utils/displayedRoute). A layout
  // effect, so whatever keys off it (the header's brands mode) re-renders in
  // the same frame as the swap, under the fade, never while the old page is
  // still visible.
  useLayoutEffect(() => {
    setDisplayedPath(displayLocation.pathname);
  }, [displayLocation.pathname]);

  // Every page opens at its top, whatever page came before: at the swap,
  // under the fade, unless the visitor went back or forward (POP), where the
  // place they left is the one to come back to. Before, a page opened at the
  // top only when the link that opened it scrolled up itself (the header's
  // section links, the product page's back arrow). A link on a scrolled home
  // page opened the catalog, the cart or /info wherever home had been
  // scrolled to, clamped to the new page's length, which was often its
  // bottom. A change of query alone (a search) is not a swap, and is left to
  // the page.
  const lastSwapPath = useRef(displayLocation.pathname);
  useLayoutEffect(() => {
    if (displayLocation.pathname === lastSwapPath.current) return;
    lastSwapPath.current = displayLocation.pathname;
    if (navigationType !== "POP") window.scrollTo(0, 0);
  }, [displayLocation.pathname, navigationType]);

  const wrapClass = phase === "out"
    ? " app-routes-wrap--out"
    : phase === "catalog-out"
      ? " app-routes-wrap--catalog-out"
      : phase === "catalog-in"
        ? " app-routes-wrap--catalog-in"
        : "";

  return (
    <div className={`app-routes-wrap${wrapClass}`}>
      <AppNavigationTypeContext.Provider value={navigationType}>
        <PageErrorBoundary resetKey={displayLocation.pathname}>
          <AppRoutes location={displayLocation} />
        </PageErrorBoundary>
      </AppNavigationTypeContext.Provider>
    </div>
  );
}

export default function App() {
  const [showTranslate, setShowTranslate] = useState(false);

  useEffect(() => {
    // Only enable Google Translate on desktop to prevent mobile crashes.
    // Same breakpoint as the CSS (utils/viewport); this used to flip at 768
    // while the stylesheet flipped at 767.
    if (!isMobileViewport()) {
      setShowTranslate(true);
    }
  }, []);

  // Drive sheen on page header lines based on mouse position (desktop only;
  // a touchscreen has no pointer to follow).
  useEffect(() => {
    if (isMobileViewport() || isTouchDevice()) return undefined;
    const onMouseMove = (e) => {
      const pct = (e.clientX / window.innerWidth) * 100;
      document.documentElement.style.setProperty("--sheen-pos", `${pct}%`);
    };
    window.addEventListener("mousemove", onMouseMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMouseMove);
  }, []);



  return (
    <AuthProvider>
      <SavedProvider>
        <AppModeProvider>
          <CartProvider>
            <AudioProvider>
            <HeaderProvider>
            {showTranslate && <GoogleTranslate />}
            <CartOverlay />
            <GlobalHeader />
            <PageTransition />
            <SearchOverlay />
            <NavFadeOverlay />
            <SearchModal />
            {MUSIC_ENABLED && <TrapVinyl />}
          </HeaderProvider>
            </AudioProvider>
          </CartProvider>
        </AppModeProvider>
      </SavedProvider>
    </AuthProvider>
  );
}

function AppRoutes({ location }) {
  return (
            <Suspense fallback={null}>
            <Routes location={location}>
              {/* Featured service */}
              <Route
                path="/featured"
                element={
                  <FeaturedSearchProvider>
                    <Outlet />
                  </FeaturedSearchProvider>
                }
              >
                <Route index element={<FeaturedPage />} />
                <Route path="search" element={<FeaturedSearchRoute />} />
                <Route path="saved" element={<SavedPage />} />
                <Route path="buy/:itemId" element={<BuyNowPage />} />
                <Route path="item/:itemId" element={<ProductPage />} />
                <Route path="cart" element={<CartPage />} />
                <Route path="checkout" element={<CheckoutPageV2 />} />
                <Route path="checkout/success" element={<CheckoutSuccessPage />} />
                <Route path="billing" element={<BillingPage />} />
                <Route path="new" element={<FeaturedPage viewMode="new" />} />
                <Route path="new/item/:itemId" element={<ProductPage />} />
                <Route path="sold" element={<FeaturedPage viewMode="sold" />} />
                <Route path="sold/item/:itemId" element={<ProductPage />} />
              </Route>

              {/* Brands index: the home brand slices as straight bands */}
              {/* Market */}
              <Route path="/market" element={<BrandsPage />} />

              {/* Proxy service - placeholder */}
              <Route path="/proxy" element={<Outlet />}>
                <Route index element={<ProxyDashboard />} />
                <Route path="quote/:requestId" element={<QuotePage />} />
                <Route path="box" element={<BoxManagementPage />} />
                <Route path="create" element={<CreateQuotePage />} />
                <Route path="search" element={<RedirectWithQuery to="/proxy" />} />
              </Route>

              {/* Legacy redirects for /new and /sold */}
              <Route path="/new" element={<RedirectWithQuery to="/featured/new" />} />
              <Route path="/new/*" element={<RedirectWithQuery to="/featured/new" />} />
              <Route path="/sold" element={<RedirectWithQuery to="/featured/sold" />} />
              <Route path="/sold/*" element={<RedirectWithQuery to="/featured/sold" />} />

              {/* Home — full-screen scroll wheel */}
              <Route path="/" element={<HomePage />} />

              {/* Showcase: the brand carousel. It was the Brands page, and
                  replaces the separate Showcase page that sat here switched
                  off (pages/ShowcasePage.jsx, no longer routed). */}
              <Route path="/showcase" element={<FeaturedGatewayPage />} />
              {/* It lived at /brands, and before that /brand; keep old links alive. */}
              <Route path="/brands" element={<RedirectWithQuery to="/showcase" />} />
              <Route path="/brand" element={<RedirectWithQuery to="/showcase" />} />
              {/* Legacy redirects */}
              <Route path="/featured-gateway" element={<RedirectWithQuery to="/showcase" />} />
              {/* Legacy catalog redirects */}
              <Route path="/catalog" element={<RedirectWithQuery to="/featured" />} />
              <Route path="/catalog/*" element={<RedirectWithQuery to="/featured" />} />
              <Route path="/catalog-gateway" element={<RedirectWithQuery to="/" />} />
              <Route path="/search" element={<RedirectWithQuery to="/featured" />} />
              <Route path="/saved" element={<RedirectWithQuery to="/featured/saved" />} />
              <Route path="/buy/:itemId" element={<RedirectWithQuery to="/featured/buy/:itemId" />} />
              <Route path="/cart" element={<RedirectWithQuery to="/featured/cart" />} />
              <Route path="/checkout" element={<RedirectWithQuery to="/featured/checkout" />} />
              <Route path="/order-success" element={<RedirectWithQuery to="/featured/checkout/success" />} />
              <Route path="/billing" element={<RedirectWithQuery to="/featured/billing" />} />

              <Route path="/admin" element={<AdminPage />} />
              <Route path="/admin/reports" element={<AdminReportsPage />} />
              <Route
                path="/admin/inventory"
                element={
                  <Suspense fallback={<div>Loading inventory...</div>}>
                    <AdminInventoryPage />
                  </Suspense>
                }
              />
              <Route path="/signin" element={<SigninPage />} />
              <Route path="/orders" element={<OrdersPage />} />
              {/* Policy pages */}
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/legal" element={<LegalPage />} />
              <Route path="/prohibited" element={<ProhibitedPage />} />
              <Route path="/storage" element={<StoragePage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/rental" element={<RentalPage />} />
              <Route path="/sell" element={<SellPage />} />
              <Route path="/authenticity" element={<AuthenticityPage />} />
              <Route path="/info" element={<InfoPage />} />
              {/* Legacy redirects: FAQ, Shipping, Returns are consolidated into /info */}
              <Route path="/faq" element={<RedirectWithQuery to="/info" />} />
              <Route path="/shipping" element={<RedirectWithQuery to="/info" />} />
              <Route path="/returns" element={<RedirectWithQuery to="/info" />} />
              {/* Admin-only routes (lazy-loaded) */}
              {/* Scraper scheduler and history routes removed - scraper disabled */}
              <Route
                path="/admin/users"
                element={
                  <Suspense fallback={<div>Loading users...</div>}>
                    <AdminUsersPage />
                  </Suspense>
                }
              />
              <Route path="/account" element={<AccountPage />} />
            </Routes>
            </Suspense>
  );
}
